class FacetsDrawerPriceRange extends HTMLElement {
  constructor() {
    super();
    this.onInput = this.onInput.bind(this);
    this.onTextChange = this.onTextChange.bind(this);
    this._submitTimer = null;
    this._lastSubmitted = '';
  }

  connectedCallback() {
    this.maxBoundCents = Number(this.dataset.rangeMaxCents) || 0;
    this.minText = this.querySelector('[data-price-input="min"]');
    this.maxText = this.querySelector('[data-price-input="max"]');
    this.minRange = this.querySelector('.facets-price-slider__input--min');
    this.maxRange = this.querySelector('.facets-price-slider__input--max');
    this.fill = this.querySelector('.facets-price-slider__fill');

    if (!this.minText || !this.maxText || !this.minRange || !this.maxRange || !this.maxBoundCents) return;

    this.minRange.max = this.maxBoundCents;
    this.maxRange.max = this.maxBoundCents;

    this.minRange.addEventListener('input', this.onInput);
    this.maxRange.addEventListener('input', this.onInput);
    this.minText.addEventListener('change', this.onTextChange);
    this.maxText.addEventListener('change', this.onTextChange);
    this.minText.addEventListener('keydown', this.onKeyDown);
    this.maxText.addEventListener('keydown', this.onKeyDown);

    this.applyStateFromInputs(false);
    this._lastSubmitted = this.getSubmitSignature();
  }

  disconnectedCallback() {
    if (this._submitTimer) clearTimeout(this._submitTimer);
    this.minRange?.removeEventListener('input', this.onInput);
    this.maxRange?.removeEventListener('input', this.onInput);
    this.minText?.removeEventListener('change', this.onTextChange);
    this.maxText?.removeEventListener('change', this.onTextChange);
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  centsToDisplay(cents) {
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return '';
    return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  }

  parseInputToCents(input) {
    const raw = String(input?.value || '')
      .replace(/[^\d.,]/g, '')
      .trim();
    if (!raw) return null;

    let normalized = raw;
    if (normalized.includes(',') && normalized.includes('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (normalized.includes(',')) {
      normalized = normalized.replace(',', '.');
    }

    const num = parseFloat(normalized);
    if (!Number.isFinite(num)) return null;
    return this.clampCents(Math.round(num * 100));
  }

  clampCents(value) {
    return Math.min(Math.max(Math.round(value), 0), this.maxBoundCents);
  }

  isMaxAtUpperBound(cents) {
    return cents === null || cents >= this.maxBoundCents;
  }

  getMinParamName() {
    if (this.minText.dataset.paramName) return this.minText.dataset.paramName;
    const name = this.minText.getAttribute('name');
    if (name) this.minText.dataset.paramName = name;
    return name;
  }

  getMaxParamName() {
    if (this.maxText.dataset.paramName) return this.maxText.dataset.paramName;
    const name = this.maxText.getAttribute('name');
    if (name) this.maxText.dataset.paramName = name;
    return name;
  }

  readMinCents() {
    return this.parseInputToCents(this.minText) ?? 0;
  }

  readMaxCents() {
    const parsed = this.parseInputToCents(this.maxText);
    if (parsed !== null) return parsed;
    return this.maxBoundCents;
  }

  syncMinInput(minCents) {
    if (minCents <= 0) {
      this.minText.value = '';
      this.minText.removeAttribute('name');
    } else {
      this.minText.setAttribute('name', this.getMinParamName() || this.minText.dataset.paramName || '');
      this.minText.value = this.centsToDisplay(minCents);
    }
  }

  syncMaxInput(maxCents) {
    if (this.isMaxAtUpperBound(maxCents)) {
      this.maxText.value = '';
      this.maxText.removeAttribute('name');
    } else {
      this.maxText.setAttribute('name', this.getMaxParamName() || this.maxText.dataset.paramName || '');
      this.maxText.value = this.centsToDisplay(maxCents);
    }
  }

  applyStateFromInputs(submit) {
    let minCents = this.readMinCents();
    let maxCents = this.readMaxCents();

    if (minCents > maxCents) maxCents = minCents;

    this.minRange.value = minCents;
    this.maxRange.value = maxCents;
    this.syncMinInput(minCents);
    this.syncMaxInput(maxCents);

    this.updateFill(minCents, maxCents);
    if (submit) this.queueSubmit();
  }

  onInput(event) {
    let minCents = Number(this.minRange.value);
    let maxCents = Number(this.maxRange.value);

    if (event.target === this.minRange && minCents > maxCents) {
      minCents = maxCents;
      this.minRange.value = minCents;
    }
    if (event.target === this.maxRange && maxCents < minCents) {
      maxCents = minCents;
      this.maxRange.value = maxCents;
    }

    this.syncMinInput(minCents);
    this.syncMaxInput(maxCents);
    this.updateFill(minCents, maxCents);
    this.queueSubmit();
  }

  onTextChange() {
    this.applyStateFromInputs(true);
  }

  updateFill(minCents, maxCents) {
    if (!this.fill || !this.maxBoundCents) return;
    const minPct = (minCents / this.maxBoundCents) * 100;
    const maxPct = (maxCents / this.maxBoundCents) * 100;
    this.fill.style.left = `${minPct}%`;
    this.fill.style.width = `${Math.max(maxPct - minPct, 0)}%`;
  }

  getSubmitSignature() {
    const min = this.minText.hasAttribute('name') ? this.minText.value.trim() : '';
    const max = this.maxText.hasAttribute('name') ? this.maxText.value.trim() : '';
    return `${min}|${max}`;
  }

  queueSubmit() {
    if (window.innerWidth < 1025 && this.closest('facet-filters-drawer')) return;
    if (this._submitTimer) clearTimeout(this._submitTimer);
    this._submitTimer = setTimeout(() => {
      const signature = this.getSubmitSignature();
      if (signature === this._lastSubmitted) return;
      this._lastSubmitted = signature;
      this.submitForm();
    }, 500);
  }

  submitForm() {
    const form = this.closest('form');
    if (!form) return;
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  resetLocal() {
    if (!this.minRange || !this.maxRange) return;
    this.minRange.value = 0;
    this.maxRange.value = this.maxBoundCents;
    this.syncMinInput(0);
    this.syncMaxInput(this.maxBoundCents);
    this.updateFill(0, this.maxBoundCents);
    this._lastSubmitted = '';
  }
}

customElements.define('facets-drawer-price-range', FacetsDrawerPriceRange);
